import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import Item from './Item';
import { cn } from '../../utils/cn';
import useHorizontalScroller from '../../hooks/useHorizontalScroller';
import * as API from '../../../api';

/**
 * "Related articles" horizontal carousel — shown at the bottom of the blog
 * detail page. Rebuilt on the home page's Categories/Featured pattern
 * (scroll-snap strip + round arrow buttons + mobile progress bar) instead of
 * the legacy MUI Carousels component.
 *
 * Data flow unchanged: API.Posts() with no page arg (first page, 9 posts).
 */

function ScrollButton({ direction, onClick, disabled }) {
  const Icon = direction === 'left' ? ChevronLeftIcon : ChevronRightIcon;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={direction === 'left' ? 'Scroll left' : 'Scroll right'}
      className={cn(
        'hidden h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-ink shadow-card transition-all duration-200 lg:flex',
        'hover:border-brand-500 hover:text-brand-500 hover:shadow-card-hover',
        disabled && 'cursor-not-allowed opacity-40 hover:border-slate-200 hover:text-ink hover:shadow-card'
      )}
    >
      <Icon className="h-5 w-5 rtl:rotate-180" />
    </button>
  );
}

const PostsCard = (props) => {
  const { t } = useTranslation();
  const [posts, setPosts] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const { scrollerRef, canPrev, canNext, progress, scrollByDir } =
    useHorizontalScroller([posts.length, loading]);

  React.useEffect(() => {
    let mounted = true;
    setLoading(true);
    API.Posts()
      .then((res) => mounted && setPosts(res?.data || []))
      .catch(() => mounted && setPosts([]))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  if (!loading && posts.length === 0) return null;

  return (
    <section className="relative bg-surface-muted py-10 sm:py-14">
      <div className="mx-auto w-full max-w-container px-4 sm:px-6 lg:px-8">
        {props.titleSection ? (
          <div className="flex items-center justify-between gap-4">
            <h2 className="font-display text-[19px] font-semibold tracking-tight text-ink sm:text-[28px] lg:text-3xl">
              {props.titleSection}
            </h2>
            <div className="flex items-center gap-2">
              <ScrollButton direction="left" onClick={() => scrollByDir('left')} disabled={!canPrev} />
              <ScrollButton direction="right" onClick={() => scrollByDir('right')} disabled={!canNext} />
            </div>
          </div>
        ) : null}

        <div className="relative mt-4 sm:mt-6">
          <div
            ref={scrollerRef}
            className="flex snap-x snap-mandatory items-stretch gap-4 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {loading
              ? Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    className="shrink-0 snap-start overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-card"
                    style={{ width: '288px' }}
                  >
                    <div className="aspect-[16/10] animate-pulse bg-slate-100" />
                    <div className="space-y-2 p-4">
                      <div className="h-3 w-2/5 animate-pulse rounded bg-slate-100" />
                      <div className="h-4 w-4/5 animate-pulse rounded bg-slate-100" />
                      <div className="h-3 w-full animate-pulse rounded bg-slate-100" />
                    </div>
                  </div>
                ))
              : posts.map((item) => (
                  <Link
                    key={item.id}
                    to={`/post/${item.id}?slug=${item.slug}`}
                    className="block w-[280px] shrink-0 snap-start sm:w-[300px]"
                  >
                    <Item data={item} />
                  </Link>
                ))}
          </div>
        </div>

        {!loading && posts.length > 0 && (
          <div className="mt-4 flex items-center justify-center gap-3 lg:hidden">
            <div className="relative h-1 w-32 overflow-hidden rounded-full bg-slate-200">
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-brand-500 transition-[width] duration-150 rtl:left-auto rtl:right-0"
                style={{ width: `${Math.max(15, progress * 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
export default PostsCard;
